import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

interface FunnelData {
  funnel: Record<string, number>;
  total_applications: number;
  metrics: { response_rate_percent: number; interview_rate_percent: number; _note: string };
  small_n_warning: boolean;
}

export default function DashboardPage() {
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<FunnelData>('/api/analytics/funnel')
      .then(setFunnel)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {loading && <p className="text-gray-500">Loading...</p>}

      {funnel && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard label="Total Applications" value={funnel.total_applications} />
          <StatCard label="Response Rate" value={`${funnel.metrics.response_rate_percent}%`} />
          <StatCard label="Interview Rate" value={`${funnel.metrics.interview_rate_percent}%`} />
        </div>
      )}

      {funnel?.small_n_warning && (
        <p className="text-sm text-amber-600 mb-4 p-3 bg-amber-50 rounded-lg" role="note">
          ⚠️ Small sample size — these numbers are preliminary, not statistically meaningful yet.
        </p>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <QuickAction to="/kb" icon="📚" title="Knowledge Base" description="Add material, run starter interview, check gaps" />
        <QuickAction to="/pipeline" icon="🔧" title="New Application" description="Ingest a job spec and generate tailored CV" />
        <QuickAction to="/tracker" icon="📋" title="Track Applications" description="View status, update progress, check deadlines" />
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </div>
  );
}

function QuickAction({ to, icon, title, description }: { to: string; icon: string; title: string; description: string }) {
  return (
    <Link to={to} className="bg-white rounded-lg border border-gray-200 p-4 hover:border-blue-300 hover:shadow-sm transition-all block">
      <div className="flex items-center gap-3">
        <span className="text-2xl" aria-hidden="true">{icon}</span>
        <div>
          <h3 className="font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </div>
    </Link>
  );
}
